'use client';

import { FC, useState, useEffect, SetStateAction } from 'react';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Box, Stack, Button, Container, Typography } from '@mui/material';

import { useLocales } from '../../../../locales';
import Iconify from '../../../../components/iconify';
import { useAuthContext } from '../../../../auth/hooks';
import Scrollbar from '../../../../components/scrollbar';
import { getCustomer } from '../../../../api/citypeople';
import { useAppContext } from '../../../../contexts/AppContext';
import { useResponsive } from '../../../../hooks/use-responsive';
import { ComponentTypeProps } from '../../../../types/page-generator';

interface ITransaction {
  id: string;
  amount: string;
  transactionType: string;
  createdAt: string;
  cardNumber: string;
  status?: string;
}

interface IMockTransaction {
  id: string;
  image: string;
  date: string;
  subDate: string;
  name: string;
  info: string;
  status: string;
  count: string;
}

interface IVirtualCard {
  id: string;
  cardNumber: string;
  transactions: ITransaction[];
}

const mockTransactions: IMockTransaction[] = [
  {
    id: '1',
    image: 'mdi:file-document-add-outline',
    date: 'Apr 14, 2025 at 09:51 AM',
    subDate: '',
    name: 'Subscription Fee',
    info: '',
    status: 'Pending',
    count: '50.00 USDT',
  },
  {
    id: '2',
    image: 'tdesign:discount',
    date: 'Mar 30, 2025 at 09:45 PM',
    subDate: 'Post date Mar 30, 2025 at 09:45 PM',
    name: 'CO-BRANDED RP CARD',
    info: 'Transaction Fee',
    status: 'Approved',
    count: '0.20 USDT',
  },
  {
    id: '3',
    image: 'fluent:tv-usb-48-regular',
    date: 'Mar 30, 2025 at 09:45 PM',
    subDate: 'Post date Mar 30, 2025 at 09:45 PM',
    name: 'eBay O*26-12873-91868 San Jose US',
    info: '',
    status: 'Canceled',
    count: '79.35 USDT',
  },
  {
    id: '4',
    image: 'mdi:file-document-add-outline',
    date: 'Mar 30, 2025 at 10:45 PM',
    subDate: 'Post date Mar 30, 2025 at 09:45 PM',
    name: 'CO-BRANDED RP CARD',
    info: 'Transaction Fee',
    status: 'Approved',
    count: '80.20 USDT',
  },
];

const CryptoExchange: FC<ComponentTypeProps> = ({ block }) => {
  const isMobile = useResponsive('down', 'sm');
  const { currentLang } = useLocales();
  const [currentTab, setCurrentTab] = useState(0);
  const [selected, setSelected] = useState('All');
  const { state, dispatch } = useAppContext();
  const { user } = useAuthContext();

  useEffect(() => {
    if (user?.id) {
      getCustomer(user.id, dispatch);
    }
  }, [user, dispatch]);

  const handleTabChange = (event: any, newValue: SetStateAction<number>) => {
    setCurrentTab(newValue);
  };

  const tabs = ['All', 'Deposit', 'Consumption', 'Refund', 'Cashback'];

  const allTransactions: ITransaction[] =
    state.customer?.virtualCards?.reduce((acc: ITransaction[], card: IVirtualCard) => {
      const cardTransactions = card.transactions.map((transaction: ITransaction) => ({
        ...transaction,
        cardNumber: card.cardNumber,
      }));
      return [...acc, ...cardTransactions];
    }, []) || [];

  const sortedTransactions = [...allTransactions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const filteredTransactions =
    selected === 'All'
      ? sortedTransactions
      : sortedTransactions.filter((transaction) => {
          switch (selected) {
            case 'Deposit':
              return transaction.transactionType === 'CREDIT';
            case 'Consumption':
              return transaction.transactionType === 'DEBIT';
            case 'Refund':
              return transaction.transactionType === 'REFUND';
            case 'Cashback':
              return transaction.transactionType === 'CASHBACK';
            default:
              return true;
          }
        });

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'CREDIT':
        return 'mdi:file-document-add-outline';
      case 'DEBIT':
        return 'tdesign:discount';
      case 'REFUND':
        return 'fluent:tv-usb-48-regular';
      case 'CASHBACK':
        return 'mdi:gift-outline';
      default:
        return 'mdi:file-document-add-outline';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  const getStatusColor = (
    transaction: ITransaction | IMockTransaction,
    isRealTransaction: boolean
  ) => {
    const status = isRealTransaction ? 'Approved' : (transaction as IMockTransaction).status;
    return status === 'Approved' ? 'green' : 'red';
  };

  const displayTransactions =
    filteredTransactions.length > 0 ? filteredTransactions : mockTransactions;

  return (
    <Container maxWidth="sm" sx={{ p: 0 }}>
      <Stack
        direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          sx={{
            my: 2,
            color: 'black',
            fontWeight: 1000,
            fontSize: '18px',
          }}
        >
          Exchange Pay Records
        </Typography>
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          sx={{ cursor: 'pointer' }}
        >
          <Typography sx={{ my: 2, color: 'red', fontSize: '14px', fontWeight: 1000 }}>
            Download Bill
          </Typography>
        </Stack>
      </Stack>
      {block?.settings?.enableTabs && (
        <Box
          sx={{
            border: '1px dashed white',
            borderRadius: '10px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: block?.settings?.tabsBackgroundColor || 'black',
            px: 2,
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            centered
            TabIndicatorProps={{
              style: {
                backgroundColor: block?.settings?.activeTabColor || 'red',
                height: '4px',
                borderRadius: '2px',
              },
            }}
            sx={{
              '& .MuiTab-root': {
                color: 'white',
                fontSize: '1.2rem',
                textTransform: 'none',
              },
              '& .Mui-selected': {
                fontWeight: 'bold',
              },
            }}
          >
            <Tab
              sx={{ color: block?.settings?.tabsColor || 'white' }}
              label="Transaction History"
            />
            <Tab
              sx={{ color: block?.settings?.tabsColor || 'white' }}
              label="Application Records"
            />
          </Tabs>
        </Box>
      )}
      {block?.settings?.enableCategories && (
        <Scrollbar
          sx={{
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            py: 1,
            my: 2,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <Stack direction="row" spacing={1} sx={{ display: 'inline-flex' }}>
            {tabs.map((tab) => (
              <Button
                key={tab}
                onClick={() => setSelected(tab)}
                variant="contained"
                disableElevation
                sx={{
                  borderRadius: '24px',
                  px: 3,
                  py: 1.5,
                  minWidth: 'fit-content',
                  backgroundColor:
                    selected === tab
                      ? block?.settings?.activeCategoriesColor || '#FF5A5F'
                      : block?.settings?.backgroundCategoriesColor || '#2A2238',
                  color: selected === tab ? 'white' : block?.settings?.categoriesColor || '#C4C4C4',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: selected === tab ? '#FF4A4F' : '#3A2F4F',
                  },
                }}
              >
                {tab}
              </Button>
            ))}
          </Stack>
        </Scrollbar>
      )}
      {block?.settings?.enableList && (
        <Stack width={1} gap={2}>
          {displayTransactions.map((transaction) => (
            <Stack
              key={transaction.id}
              direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
              width={1}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                background: block?.settings?.backgroundListColor || '#2b1d36',
                borderRadius: '20px',
                p: 2,
                py: 4,
                position: 'relative',
              }}
            >
              <Box
                sx={
                  currentLang.value === 'he'
                    ? {
                        position: 'absolute',
                        top: 0,
                        left: '50px',
                        background: getStatusColor(transaction, filteredTransactions.length > 0),
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                        p: 0.5,
                      }
                    : {
                        position: 'absolute',
                        top: 0,
                        right: '50px',
                        background: getStatusColor(transaction, filteredTransactions.length > 0),
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                        p: 0.5,
                      }
                }
              >
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: '8px',
                  }}
                >
                  {filteredTransactions.length > 0
                    ? (transaction as ITransaction).status || 'Approved'
                    : (transaction as IMockTransaction).status}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  background: block?.settings?.listIconBackgroundColor || 'black',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify
                  icon={
                    filteredTransactions.length > 0
                      ? getTransactionIcon((transaction as ITransaction).transactionType)
                      : (transaction as IMockTransaction).image
                  }
                  sx={{
                    color: block?.settings?.listTextColor || 'white',
                    fontSize: 24,
                  }}
                />
              </Box>
              <Stack gap={1} flex={1} mx={5}>
                <Stack>
                  <Typography
                    sx={{
                      color: block?.settings?.listTextColor || 'white',
                      fontSize: isMobile ? '14px' : '12px',
                      fontWeight: 1000,
                    }}
                  >
                    {filteredTransactions.length > 0
                      ? formatDate((transaction as ITransaction).createdAt)
                      : (transaction as IMockTransaction).date}
                  </Typography>
                  {!filteredTransactions.length && (transaction as IMockTransaction).subDate && (
                    <Typography
                      sx={{
                        color: block?.settings?.listTextColor || '#F5F5F5',
                        fontSize: isMobile ? '12px' : '10px',
                      }}
                    >
                      {(transaction as IMockTransaction).subDate}
                    </Typography>
                  )}
                </Stack>
                <Stack>
                  <Typography
                    sx={{
                      color: block?.settings?.listTextColor || '#F5F5F5',
                      fontSize: isMobile ? '14px' : '12px',
                      fontWeight: 1000,
                    }}
                  >
                    {filteredTransactions.length > 0
                      ? (transaction as ITransaction).cardNumber
                      : (transaction as IMockTransaction).name}
                  </Typography>
                  <Typography
                    sx={{
                      color: block?.settings?.listTextColor || '#F5F5F5',
                      fontSize: isMobile ? '14px' : '12px',
                    }}
                  >
                    {filteredTransactions.length > 0
                      ? (transaction as ITransaction).transactionType
                      : (transaction as IMockTransaction).info}
                  </Typography>
                </Stack>
              </Stack>
              <Typography sx={{ color: block?.settings?.listTextColor || 'white' }}>
                {filteredTransactions.length > 0
                  ? `${(transaction as ITransaction).amount} USD`
                  : (transaction as IMockTransaction).count}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Container>
  );
};

export default CryptoExchange;
